import { useEffect } from 'react'
import {
  knowledgeApi,
  type ProductBelief,
  type ProductKnowledge as ProductKnowledgeData,
  type ProductKnowledgeGap,
} from '../../services/api'
import BeliefRows, { useBeliefRead, type BeliefRowsState, type BeliefSubject } from './BeliefRows'
import { refusalFor } from './refusal'
import {
  Badge,
  Card,
  CardHeader,
  CardList,
  Chip,
  ChipGroup,
  EmptyState,
  EmptyValue,
  ErrorState,
  KeyValue,
  LoadingState,
  Meta,
  ReadingSection,
} from '../../ds'

/**
 * What Marva knows about one product (App IA v5 phase 5b, MAR-204 PR 3).
 *
 * ── The record moves; the index does not ────────────────────────────────────────────────
 *
 * §9.1: the six knowledge routes are all by-id, deliberately — there is no `list` among
 * them. So the catalogue index stays on `/data/dynamodb/products` and the *record* is read
 * here, from `GET …/knowledge/products/{productId}`.
 *
 * ⚠️ That move is a tenancy fix as well as an IA one. `dataService.getProductById` reaches
 * `shopify-products`, which is keyed `PRODUCT#<id>` with **no workspace in the key** — the
 * handler takes a `workspaceId` and cannot enforce it. The knowledge route reads the
 * expert-card partition instead, `PROD#<accountId>#<workspaceId>`, with the `accountId`
 * resolved server-side from the workspace row rather than taken from the request.
 *
 * ── The card arrives WHOLE, and is rendered whole ───────────────────────────────────────
 *
 * There is no `ProductExpertCardSchema`. `readProductKnowledge` returns `res.Item` as it
 * stands, so any field a writer adds reaches this surface by default — and a fixed render
 * would quietly drop it. Everything below walks the object it was given; the only names
 * this file knows are `pk` and `sk`, which are the row's storage keys, and
 * `confidenceScore`, the completeness rubric that must never be shown (`NOT_KNOWLEDGE`) —
 * none of them knowledge about a product. A field added to the card tomorrow appears here with no change to this
 * file, which is the property being protected (`CardValue`, and the test that plants a
 * field no version of this screen has heard of).
 *
 * ── `summaryEmpty`, and the string that means the opposite of itself ────────────────────
 *
 * ⚠️ `buildSummary` returns `''` for a card whose sections are all empty. At the string
 * level that is identical to "no card", and the two mean opposite things to a merchant:
 * one is *she has nothing on this product*, the other is *she has a card and it says
 * nothing yet*. The server states which rather than leaving it to be inferred, and this is
 * the consumer that has to keep them apart. A 404 (`product_card_not_found`) is the first;
 * `summaryEmpty: true` is the second.
 *
 * ── Three sections (MAR-326) ────────────────────────────────────────────────────────────
 *
 * **What she believes** — product beliefs on the shared `BeliefRows`, verdicts through
 * `settleProductBelief`. ⚠️ `beliefsUnavailable` is its own state: the server answers
 * `beliefs: null` rather than a 500 when only the belief partition failed, so the card is
 * not lost with it — and "could not read" must not render as "believes nothing".
 * **What she has read** — the summary, then the card's sections as `read` rows.
 * **What the card is missing** — `gaps`, heaviest first, in words only. ⚠️ No weight and
 * no %: the weights are COMPLETENESS, the rubric `confidenceScore` is summed with, and
 * neither is confidence (spec decision 4). The server's order carries the weight already.
 *
 * ── Read-only card, and that is a decision ──────────────────────────────────────────────
 *
 * No merchant-notes box. `card.merchant.*` is read by `buildSummary` and written by
 * nothing, and it is worth **+0.20 of every card's `confidenceScore`**
 * (`card-synthesizer/src/index.js:198-210`) — so a notes field here would be an
 * agent-behaviour change wearing a form, and it owes `ProductExpertCardSchema` first
 * (§8.1, §9.4). Deferred deliberately, not forgotten.
 *
 * ── Three read states, plus one that is not a failure ───────────────────────────────────
 *
 * Loading · refused · read. And a 404 is a fourth: it is the ordinary state of a product
 * she has not got to yet, so it renders as an empty record rather than as a refusal —
 * which is why `product_card_not_found` is NOT mapped in `knowledgeRefusal`. There is one
 * copy of that sentence and it is here, where the distinction is drawn.
 *
 * ── Scoping ─────────────────────────────────────────────────────────────────────────────
 *
 * The same rule as `CustomerKnowledge`, and now the same code: the read is `useBeliefRead`
 * (MAR-326), whose every cached value names the workspace AND the product and is matched at
 * the READ — the product read gained a write path (verdicts), and with it the in-flight set
 * and the feedback this file once had no need of.
 */

/** The product rail. Module-level: its identity is a hook dependency. */
const PRODUCT_SUBJECT: BeliefSubject<ProductBelief, ProductKnowledgeData> = {
  idOf: (b) => b.beliefId,
  read: async(ws, id, opts) => {
    const res = await knowledgeApi.getProductKnowledge(ws, id, opts)
    if (res.success && res.data) return { kind: 'read', data: res.data }
    const reason = (res.details as { reason?: unknown } | undefined)?.reason
    // ⚠️ A 404 is not a refusal to the merchant. It is the ordinary state of a product
    // she has not synthesised yet, and it is the ONE reading that must not be confused
    // with `summaryEmpty`.
    if (reason === 'product_card_not_found') return { kind: 'missing' }
    return { kind: 'failed', refusal: refusalFor(res, 'Could not read what she knows about this product.') }
  },
  settle: (ws, id, beliefId, body) => knowledgeApi.settleProductBelief(ws, id, beliefId, body),
  beliefsOf: (d) => ({ beliefs: d.beliefs, counts: d.counts }),
  withBeliefs: (d, beliefs, counts) => ({ ...d, beliefs, counts }),
}

/**
 * A human name for each gap section. The keys are `CARD_SECTIONS` in
 * `@marva/shared/expert-card-confidence`; an unknown one falls back to its last segment,
 * humanised, so a section added there still reads as words here.
 */
const GAP_LABEL: Record<string, string> = {
  extracted: 'Details from your product page',
  'webResearch.ratings': 'Ratings',
  'webResearch.materialsAndCompetitors': 'Materials and alternatives',
  'webResearch.expertReviews': 'Expert reviews',
  visual: 'Visual',
  returns: 'Returns',
  merchant: 'From you',
}
export function gapLabel(section: string): string {
  return GAP_LABEL[section] ?? humanise(section.split('.').pop() || section)
}

/** What the drawer's header may take from the card on a cold open. Facts the card holds, no more. */
export interface CardHeader {
  title: string | null
  productType: string | null
}
const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null)

/**
 * Not knowledge about the product, so never a card row. The ONLY names this file knows —
 * see the docblock.
 *  - `pk`, `sk`: the row's storage keys.
 *  - `confidenceScore`: the COMPLETENESS rubric `card-synthesizer` stamps onto the row
 *    (`updateExpertCard`). ⚠️ It is not confidence and the spec forbids showing it in any
 *    form (decision 4); "What the card is missing" says the same thing in words. Rendered as
 *    a row it read "Confidence score — 0.55", a number about the product that claims a
 *    certainty nobody measured.
 */
const NOT_KNOWLEDGE: ReadonlySet<string> = new Set(['pk', 'sk', 'confidenceScore'])

/** `keySellingPoints` → `Key selling points`. Unknown fields get a readable heading. */
export function humanise(key: string): string {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
  if (!words) return key
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase()
}

/** Is this something to print as-is rather than walk into? */
function isScalar(v: unknown): v is string | number | boolean {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
}

/**
 * Anything, rendered as what it is.
 *
 * ⚠️ Nothing here names a card field. It dispatches on the SHAPE of the value, so a
 * section the synthesiser adds next month renders without this file changing — which is
 * the whole contract of a schemaless record. Empty containers say so out loud rather than
 * rendering as a blank, because "she has this section and it is empty" is a fact.
 */
function CardValue({ value }: { value: unknown }) {
  if (value == null || value === '') return <EmptyValue />
  if (typeof value === 'boolean') return <span>{value ? 'yes' : 'no'}</span>
  if (isScalar(value)) return <span>{String(value)}</span>

  if (Array.isArray(value)) {
    if (value.length === 0) return <EmptyValue>nothing recorded</EmptyValue>
    if (value.every(isScalar)) {
      return (
        <ChipGroup>
          {value.map((v, i) => (
            <Chip key={i}>{String(v)}</Chip>
          ))}
        </ChipGroup>
      )
    }
    return (
      <CardList>
        {value.map((v, i) => (
          <div key={i}>
            <CardValue value={v} />
          </div>
        ))}
      </CardList>
    )
  }

  const entries = Object.entries(value as Record<string, unknown>)
  if (entries.length === 0) return <EmptyValue>nothing recorded</EmptyValue>
  return <KeyValue items={entries.map(([k, v]) => ({ label: humanise(k), value: <CardValue value={v} /> }))} />
}

export default function ProductKnowledge({
  workspaceId,
  productId,
  onCard,
}: {
  workspaceId?: string
  productId?: string
  /** The card's own title and type once it lands for THIS record, else `null`. */
  onCard?: (workspaceId: string, productId: string, header: CardHeader | null) => void
}) {
  const read = useBeliefRead(PRODUCT_SUBJECT, workspaceId, productId)
  const { current, prior, lastRead } = read
  // ⚠️ The card, the summary and the gaps do not change with the settled toggle, so they
  // render from the last SUCCESSFUL read: they survive the frame the wider read is in
  // flight, and a wider read that FAILS costs the beliefs list alone. The beliefs do change,
  // so they come from `current` alone.
  const card = lastRead?.data?.card ?? null
  const title = str(card?.title)
  const productType = str(card?.productType)

  useEffect(() => {
    if (workspaceId && productId) onCard?.(workspaceId, productId, card ? { title, productType } : null)
  }, [onCard, workspaceId, productId, card, title, productType])

  if (!workspaceId || !productId) {
    // A row with no id is not a record this API can be asked about, and a request built
    // from a blank id would address the workspace's whole product path.
    return null
  }

  // Nothing has been read successfully yet: say what the latest attempt says.
  if (!lastRead && !prior) {
    return (
      <ReadingSection label="What she knows" testId="kn-card">
        <LoadingState caption="Reading what she knows about this product…" />
      </ReadingSection>
    )
  }

  if (!lastRead && prior?.failed) {
    return (
      <ReadingSection label="What she knows" testId="kn-card">
        <ErrorState
          title={
            prior.refusal ||
            'Could not read what she knows about this product, so nothing is shown rather than a guess. Reload to try again.'
          }
        />
      </ReadingSection>
    )
  }

  if (!lastRead && prior?.missing) {
    // ⚠️ No beliefs section either: the 404 is answered before the belief partition is
    // read, and a verdict on a product with no card is itself a 404 — so there is nothing
    // to show there and nothing a button could do.
    return (
      <ReadingSection label="What she knows" testId="kn-card">
        <EmptyState size="sm">
          She has not built an expert card for this product yet, so she knows it only as a
          line in your catalogue. Cards come from what she reads about a product; there is
          nothing here until there is.
        </EmptyState>
      </ReadingSection>
    )
  }

  const data = (lastRead ?? prior)?.data as ProductKnowledgeData
  const { summary, summaryEmpty } = data
  const gaps: ProductKnowledgeGap[] = data.gaps || []
  const entries = Object.entries(card || {}).filter(([k]) => !NOT_KNOWLEDGE.has(k))

  const now = current?.data ?? null
  const beliefState: BeliefRowsState<ProductBelief> = !current
    ? { kind: 'loading' }
    : current.failed && current.refusal
      ? // The wider read itself was refused, after a narrower one landed: its refusal,
        // under the beliefs — the card above it is the read that worked.
        { kind: 'failed', text: current.refusal }
      : !now || now.beliefsUnavailable || now.beliefs == null
      ? {
          kind: 'failed',
          // ⚠️ `beliefs: null` is "could not read", never "none" — the server sends null,
          //    not `[]`, precisely so this sentence can be said.
          text: 'She couldn’t read her beliefs about this product just now — not shown, rather than shown as none.',
        }
      : { kind: 'ready', beliefs: now.beliefs, counts: now.counts }

  return (
    <>
      <BeliefRows
        read={read}
        state={beliefState}
        loadingText="Reading what she believes…"
        noneText="She has not formed a theory about this product yet. Theories come from what shoppers do and ask about it, so there is nothing here until there is evidence for something."
      />

      <ReadingSection label="What she has read" testId="kn-card">
        {/* The string the AGENT is handed, from the shared `buildSummary` — not a second
            rendering of the card. A paraphrase here would show the merchant something the
            agent never reads. */}
        {summaryEmpty ? (
          <EmptyState size="sm">
            She has a card for this product and every section of it is still empty, so she is
            handed nothing about it when she speaks to a shopper.
          </EmptyState>
        ) : (
          <ReadRow k="summary">
            <p data-testid="kn-summary">{summary}</p>
          </ReadRow>
        )}

        {entries.length > 0 && (
          <CardList data-testid="kn-card-sections">
            {entries.map(([k, v]) => (
              // The section's own heading is its key here; a second, mono copy of it is noise.
              <ReadRow key={k} title={humanise(k)}>
                <CardValue value={v} />
              </ReadRow>
            ))}
          </CardList>
        )}

        {/* ⚠️ Read-only, deliberately. See the docblock: `card.merchant.*` is worth +0.20 of
            every card's confidence score, so a notes box is an agent-behaviour change. */}
        <p>
          This is hers to write. She builds it from the catalogue and what she reads about the
          product, and there is nothing here for you to correct.
        </p>
      </ReadingSection>

      {gaps.length > 0 && (
        <ReadingSection label="What the card is missing" testId="kn-gaps">
          {/* Heaviest first, as the server ordered them. ⚠️ The weight is never printed. */}
          <CardList>
            {gaps.map((g) => (
              <Card
                padding="sm"
                tone={g.fillable ? 'plain' : 'recessed'}
                key={g.section}
                data-testid="kn-gap"
                data-fillable={g.fillable ? 'true' : 'false'}
              >
                <CardHeader
                  titleAs="span"
                  title={gapLabel(g.section)}
                  description={g.fillable ? 'She can learn this — not on the card yet.' : 'Nothing teaches her this yet.'}
                />
              </Card>
            ))}
          </CardList>
        </ReadingSection>
      )}
    </>
  )
}

/**
 * One thing she has READ — a fact off the card, with no write path. It carries the `read`
 * chip and its provenance rather than a verdict, because there is nothing to tell her.
 */
function ReadRow({ k, title, children }: { k?: string; title?: string; children: React.ReactNode }) {
  return (
    <Card padding="sm" data-testid="kn-read">
      {(title || k) && (
        <CardHeader
          titleAs="span"
          title={title}
          aside={
            k ? (
              <Badge mono data-testid="kn-key">
                {k}
              </Badge>
            ) : undefined
          }
        />
      )}
      {children}
      <ChipGroup>
        <Chip data-source="read">read</Chip>
        <Meta data-testid="kn-meta">From the expert card</Meta>
      </ChipGroup>
    </Card>
  )
}
